import { useEffect, useState } from "react";
import { BadgeCheck, GitCompareArrows } from "lucide-react";
import type {
  Vehicle,
  VehicleModelSummary,
  VehicleVersion,
} from "../../../../types";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui";
import ImageFallback from "../../../../components/ImageFallback/ImageFallback";
import { formatNote } from "../../../../utils/formatNote";
import { trackEvent } from "../../../../utils/analytics";

type VehicleHeaderProps = {
  vehicle: Vehicle;
  vehicleModel?: { id: string; name: string; slug: string } | null;
  currentVersion?: VehicleVersion;
  models: VehicleModelSummary[];
  versions: VehicleVersion[];
  photoUrl: string | null;
  testDriveUrl: string | null;
  reviewsAnchor: string;
  compare: {
    visible: boolean;
    isCompared: boolean;
    onToggle: () => void;
  };
  onModelChange: (slug: string) => void;
  onVersionChange: (id: string) => void;
};

const RESET_VALUE = "__all__";

const reviewsLabel = (count: number) => {
  if (count === 0) return "Aucun avis pour le moment";
  if (count === 1) return "1 avis propriétaire certifié";
  return `${count} avis de propriétaires certifiés`;
};

const VehicleHeader = (props: VehicleHeaderProps) => {
  const {
    vehicle,
    vehicleModel,
    currentVersion,
    models,
    versions,
    photoUrl,
    testDriveUrl,
    reviewsAnchor,
    compare,
    onModelChange,
    onVersionChange,
  } = props;

  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [photoUrl]);

  const handleTestDriveClick = () => {
    trackEvent({
      name: "Demander un essai",
      params: {
        category: "Interaction",
        action: "Click",
        label: "Demander un essai",
        page_type: "Fiche véhicule",
        vehicle: `${vehicle.brand.title} ${vehicle.name}`,
        vehicle_model: vehicleModel?.name,
        version: currentVersion?.sra_commercial,
      },
    });
  };

  const reviewsCount = vehicle.reviews.length;
  const hasReviews = reviewsCount > 0;
  const showSelects = models.length > 0 || !!vehicleModel;
  const showVersionSelect = !!vehicleModel && versions.length > 1;

  return (
    <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[1fr_1.1fr]">
      <div className="lg:order-2">
        {imageError || !photoUrl ? (
          <ImageFallback />
        ) : (
          <img
            src={photoUrl}
            alt={`${vehicle.brand.title} ${vehicle.name}`}
            className="block h-auto w-full rounded-[18px]"
            onError={() => setImageError(true)}
          />
        )}
      </div>

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2.5">
          <span className="text-xs font-bold uppercase tracking-[0.1em] text-primary">
            {vehicle.brand.title}
          </span>
          <div className="flex flex-wrap items-center justify-start gap-5">
            <h1 className="m-0 text-3xl! leading-[1.05] font-bold lg:text-[44px]!">
              {vehicle.name}
            </h1>
            {showSelects && (
              <div className="flex shrink-0 gap-2.5">
                <Select
                  onValueChange={(value) =>
                    onModelChange(value === RESET_VALUE ? "" : value)
                  }
                >
                  <SelectTrigger size="sm">
                    <SelectValue
                      placeholder={
                        vehicleModel ? vehicleModel.name : "Tous les modèles"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicleModel && (
                      <SelectItem key="allModeles" value={RESET_VALUE}>
                        Tous les modèles
                      </SelectItem>
                    )}
                    {models.map((model) => (
                      <SelectItem key={model.slug} value={model.slug}>
                        {model.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {showVersionSelect && (
                  <Select
                    onValueChange={(value) =>
                      onVersionChange(value === RESET_VALUE ? "" : value)
                    }
                  >
                    <SelectTrigger size="sm">
                      <SelectValue
                        placeholder={
                          currentVersion
                            ? currentVersion.sra_commercial
                            : "Toutes les versions"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {currentVersion && (
                        <SelectItem key="allVersions" value={RESET_VALUE}>
                          Toutes les versions
                        </SelectItem>
                      )}
                      {versions.map((version) => (
                        <SelectItem key={version.id} value={version.id}>
                          {version.sra_commercial}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3.5">
          {hasReviews && (
            <span className="text-5xl leading-none font-bold text-primary">
              {formatNote(vehicle.note)}
              <small className="text-xl font-normal text-muted-foreground">
                {" "}
                / 10
              </small>
            </span>
          )}
          <span
            className={`mt-1 inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-medium ${
              hasReviews
                ? "bg-green-100 text-green-800"
                : "bg-slate-100 text-slate-600"
            }`}
          >
            {hasReviews && <BadgeCheck className="size-3.5" />}
            {reviewsLabel(reviewsCount)}
          </span>
        </div>

        <div className="flex w-full flex-col gap-3 pt-1 sm:w-fit">
          <div className="flex gap-3">
            <Button asChild>
              <a href={reviewsAnchor}>Voir les avis</a>
            </Button>
            {compare.visible && (
              <Button
                variant="outline"
                onClick={compare.onToggle}
                aria-pressed={compare.isCompared}
              >
                <GitCompareArrows className="size-4" />
                {compare.isCompared ? "Retirer de la comparaison" : "Comparer"}
              </Button>
            )}
          </div>
          {testDriveUrl && (
            <Button asChild className="w-full">
              <a
                href={testDriveUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleTestDriveClick}
              >
                Demander un essai
              </a>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleHeader;
